// frontend/src/components/Layout/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useTenant } from "../../context/TenantContext";
import {
  FiShoppingBag,
  FiMenu,
  FiX,
  FiUser,
  FiLogOut,
  FiUserPlus,
  FiGrid,
  FiPackage,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiMail,
  FiSearch,
  FiTag,
  FiAlertTriangle,
  FiBell,
  FiChevronDown,
} from "react-icons/fi";
import LogoutModal from "../Common/LogoutModal";
import { formatPrice } from "../../utils/format";
import { getStorefrontThemeConfig } from "../../utils/storefrontTheme";
import {
  DEFAULT_STORE_BRANDING,
  STORE_BRAND_SEPARATORS,
  STORE_NAME_FONT_FAMILIES,
} from "../../utils/storeBranding";
import "../../styles/navbar.css";

const MOBILE_BREAKPOINT = 768;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const DEFAULT_NAV_LINKS = [
  { id: "home", label: "Home", path: "/" },
  { id: "shop", label: "Shop", path: "/shop" },
  { id: "about", label: "About", path: "/about" },
  { id: "contact", label: "Contact", path: "/contact" },
];
const ADMIN_ROLES = ["Admin", "TenantAdmin", "SuperAdmin"];

const pascal = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const validMenuColor = (value, fallback) =>
  value === "transparent" ||
  (typeof value === "string" && HEX_COLOR.test(value))
    ? value
    : fallback;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [openNavigationDropdown, setOpenNavigationDropdown] = useState(null);
  const [isUpdatingItem, setIsUpdatingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [signedOutNotice, setSignedOutNotice] = useState("");
  const [logoFailed, setLogoFailed] = useState(false);

  const userDropdownRef = useRef(null);
  const cartDropdownRef = useRef(null);
  const menuToggleRef = useRef(null);
  const menuCloseRef = useRef(null);
  const wasMenuOpen = useRef(false);

  const { user, logout } = useAuth();
  const {
    cartItems,
    cartCount,
    cartTotal,
    cartSavings,
    expiringPromotions,
    updateQuantity,
    removeFromCart,
    loading,
    loadingItems,
  } = useCart();
  const { tenant, settings } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setLogoFailed(false);
  }, [tenant?.Logo]);

  /* ------------------------------------------------------------------ */
  /* Theme / layout configuration                                       */
  /* ------------------------------------------------------------------ */
  const storefrontConfig = getStorefrontThemeConfig(
    settings?.storefrontTemplate,
    settings?.storefrontThemeConfig,
  );
  const mobileLayout = storefrontConfig.storefrontContent.mobileLayout;
  const navigationContent = storefrontConfig.storefrontContent;
  const navigationStyle = navigationContent.navigationStyle || "underline";
  const navigationAnimation =
    navigationContent.navigationAnimation || "underline";
  const dropdownStyle = navigationContent.dropdownStyle || "classic";
  const menuTemplate =
    mobileLayout.menuTemplate === "shopify-drawer"
      ? "editorial-drawer"
      : mobileLayout.menuTemplate || "classic";

  // Role-aware destinations
  const ordersDestination =
    user?.role === "Admin" ? "/admin/orders" : "/orders";
  const notificationsDestination = ADMIN_ROLES.includes(user?.role)
    ? "/admin/notifications"
    : "/notifications";

  /* ------------------------------------------------------------------ */
  /* Open / close helpers                                               */
  /* ------------------------------------------------------------------ */
  const closeAll = () => {
    setIsMenuOpen(false);
    setShowUserDropdown(false);
    setShowCartDropdown(false);
    setOpenNavigationDropdown(null);
  };

  // Close menus and return to the top of the page on navigation
  const handleNavClick = () => {
    closeAll();
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  const handleNavigate = (path) => {
    handleNavClick();
    navigate(path);
  };

  const handleStoreSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    handleNavigate(
      query ? `/shop?search=${encodeURIComponent(query)}` : "/shop",
    );
    setSearchQuery("");
  };

  // Also close on browser back/forward
  useEffect(() => {
    closeAll();
  }, [location.pathname]);

  // Elevated shadow once the page scrolls
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close popovers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!userDropdownRef.current?.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (!cartDropdownRef.current?.contains(event.target)) {
        setShowCartDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleSignedOut = (event) => {
      setSignedOutNotice(event.detail?.message || "You have been signed out.");
    };
    window.addEventListener("auth-signed-out", handleSignedOut);
    return () => window.removeEventListener("auth-signed-out", handleSignedOut);
  }, []);

  // Escape closes everything
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") closeAll();
    };
    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, []);

  // Never leave the drawer open (and scroll locked) after growing to desktop
  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT + 1}px)`);
    const handleChange = (event) => {
      if (event.matches) setIsMenuOpen(false);
    };
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.classList.toggle("mobile-menu-open", isMenuOpen);
    return () => document.body.classList.remove("mobile-menu-open");
  }, [isMenuOpen]);

  // Focus management for the drawer
  useEffect(() => {
    if (isMenuOpen) {
      menuCloseRef.current?.focus();
    } else if (wasMenuOpen.current) {
      menuToggleRef.current?.focus();
    }
    wasMenuOpen.current = isMenuOpen;
  }, [isMenuOpen]);

  /* ------------------------------------------------------------------ */
  /* Actions                                                            */
  /* ------------------------------------------------------------------ */
  const handleLogout = async (clearLastUser) => {
    setIsLoggingOut(true);
    try {
      await Promise.all([
        logout("Logged out successfully", { clearLastUser }),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
      navigate("/");
      setShowLogoutModal(false);
      handleNavClick();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleUpdateQuantity = async (productId, currentQuantity, delta) => {
    const newQuantity = currentQuantity + delta;
    setIsUpdatingItem(productId);
    try {
      if (newQuantity <= 0) {
        await removeFromCart(productId);
      } else {
        await updateQuantity(productId, newQuantity);
      }
    } finally {
      setIsUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (productId) => {
    setIsUpdatingItem(productId);
    try {
      await removeFromCart(productId);
    } finally {
      setIsUpdatingItem(null);
    }
  };

  const toggleUserDropdown = () => {
    setShowCartDropdown(false);
    setShowUserDropdown((open) => !open);
    setIsMenuOpen(false);
  };

  const toggleCartDropdown = () => {
    setShowUserDropdown(false);
    setShowCartDropdown((open) => !open);
    setIsMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen((open) => !open);
    setShowUserDropdown(false);
    setShowCartDropdown(false);
  };

  const toggleNavigationDropdown = (linkId) =>
    setOpenNavigationDropdown((current) =>
      current === linkId ? null : linkId,
    );

  /* ------------------------------------------------------------------ */
  /* Navigation data                                                    */
  /* ------------------------------------------------------------------ */
  const isActive = (path) =>
    location.pathname === path ||
    (path !== "/" && location.pathname.startsWith(`${path}/`));

  const navLinks = (navigationContent.navigation || DEFAULT_NAV_LINKS).filter(
    (link) => link.enabled !== false,
  );
  const rootNavLinks = navLinks.filter(
    (link) => !navLinks.some((item) => item.id === link.parentId),
  );
  const getNavChildren = (link) =>
    navLinks.filter((item) => item.parentId === link.id);

  /* ------------------------------------------------------------------ */
  /* Cart helpers                                                       */
  /* ------------------------------------------------------------------ */
  const dropdownItems = cartItems.slice(0, 4);
  const remainingCount = cartItems.length - dropdownItems.length;

  const isItemUpdating = (productId) =>
    isUpdatingItem === productId || loadingItems[productId];

  const hasPromotion = (item) =>
    item.OriginalPrice &&
    item.OriginalPrice > (item.UnitPrice || item.Price || 0);

  const getDiscountPercent = (item) => {
    if (!hasPromotion(item)) return 0;
    const original = parseFloat(item.OriginalPrice) || 0;
    const current = parseFloat(item.UnitPrice || item.Price) || 0;
    if (original === 0) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  const defaults = DEFAULT_STORE_BRANDING;
  const getBrandValue = (camelCaseKey, fallback) =>
    settings?.[pascal(camelCaseKey)] ?? settings?.[camelCaseKey] ?? fallback;
  const getBrandFont = (key) => {
    const fontKey = getBrandValue(key, defaults[key]);
    return (
      STORE_NAME_FONT_FAMILIES[fontKey]?.family ||
      STORE_NAME_FONT_FAMILIES[defaults[key]].family
    );
  };
  const getBrandColor = (key) => {
    const color = getBrandValue(key, defaults[key]);
    return HEX_COLOR.test(color) ? color : defaults[key];
  };
  const getBrandFlag = (key) => {
    const value = getBrandValue(key, defaults[key]);
    return value === true || value === "true" || value === 1 || value === "1";
  };
  const getBrandSize = (key, min, max) => {
    const size = Number(getBrandValue(key, defaults[key]));
    return Number.isFinite(size) && size >= min && size <= max
      ? size
      : defaults[key];
  };

  // One text block (line 1, line 2 or slogan) -> its CSS custom properties
  const textStyleVars = (cssName, key, minSize, maxSize) => ({
    [`--store-${cssName}-font`]: getBrandFont(`${key}FontFamily`),
    [`--store-${cssName}-size`]: `${getBrandSize(`${key}FontSize`, minSize, maxSize)}px`,
    [`--store-${cssName}-color`]: getBrandColor(`${key}Color`),
    [`--store-${cssName}-weight`]: getBrandFlag(`${key}Bold`) ? "700" : "400",
    [`--store-${cssName}-style`]: getBrandFlag(`${key}Italic`)
      ? "italic"
      : "normal",
    [`--store-${cssName}-decoration`]: getBrandFlag(`${key}Underline`)
      ? "underline"
      : "none",
  });

  const configuredBrandLayout =
    settings?.StoreBrandLayout || settings?.storeBrandLayout;
  const brandLayout = [
    "horizontal",
    "stacked",
    "logo-only",
    "name-only",
  ].includes(configuredBrandLayout)
    ? configuredBrandLayout
    : defaults.storeBrandLayout;
  const effectiveBrandLayout =
    brandLayout === "logo-only" && (!tenant?.Logo || logoFailed)
      ? "name-only"
      : brandLayout;
  const brandNameLayout =
    (settings?.StoreNameLayout || settings?.storeNameLayout) === "stacked"
      ? "stacked"
      : "horizontal";
  const sloganLayout =
    (settings?.StoreSloganLayout || settings?.storeSloganLayout) === "beside"
      ? "beside"
      : "below";
  const separatorGlyph =
    STORE_BRAND_SEPARATORS[
      getBrandValue("storeBrandSeparator", defaults.storeBrandSeparator)
    ] || "";

  const brandStyles = {
    ...textStyleVars("name-line1", "storeNameLine1", 14, 44),
    ...textStyleVars("name-line2", "storeNameLine2", 14, 44),
    ...textStyleVars("slogan", "storeSlogan", 10, 24),
    "--store-brand-separator-color": getBrandColor("storeBrandSeparatorColor"),
    "--store-logo-size": `${getBrandSize("storeLogoSize", 28, 100)}px`,
  };

  const storeNameLine1 =
    getBrandValue("storeNameLine1", "") || tenant?.BusinessName || "";
  const storeNameLine2 = getBrandValue("storeNameLine2", "") || "";
  const storeSlogan = getBrandValue("storeSlogan", "") || "";

  const userDisplayName = user?.name || user?.userName || "";
  const userInitial = (userDisplayName || user?.email || "?").trim().charAt(0);

  return (
    <>
      <nav
        className={`navbar ${scrolled ? "scrolled" : ""}`}
        aria-label="Main"
        data-navigation-style={navigationStyle}
        data-navigation-animation={navigationAnimation}
        data-dropdown-style={dropdownStyle}
        data-mobile-header-layout={mobileLayout.headerLayout}
        data-mobile-header-background={mobileLayout.headerBackground}
        data-mobile-menu-side={mobileLayout.menuSide}
        data-mobile-menu-template={menuTemplate}
        data-mobile-menu-overlay={mobileLayout.menuOverlay}
        data-mobile-logo-size={mobileLayout.logoSize}
        data-brand-layout={effectiveBrandLayout}
        data-brand-name-layout={brandNameLayout}
        data-brand-slogan-layout={sloganLayout}
        style={{
          ...brandStyles,
          "--mobile-menu-accent": HEX_COLOR.test(
            storefrontConfig.primaryColor || "",
          )
            ? storefrontConfig.primaryColor
            : "#2c7da0",
          "--mobile-menu-background": validMenuColor(
            mobileLayout.menuBackgroundColor,
            "#ffffff",
          ),
          "--mobile-menu-text": validMenuColor(
            mobileLayout.menuTextColor,
            "#202223",
          ),
        }}
      >
        <div className="nav-container">
          {/* Brand */}
          <div className="nav-logo">
            <Link
              to="/"
              className="nav-logo-link"
              onClick={handleNavClick}
              aria-label={`${tenant?.BusinessName || "Store"} home`}
            >
              {loading ? (
                <div className="logo-placeholder loading" aria-hidden="true" />
              ) : tenant?.Logo && !logoFailed ? (
                <img
                  src={tenant.Logo}
                  alt={tenant.BusinessName || "Store"}
                  className="logo-image"
                  onError={() => setLogoFailed(true)}
                />
              ) : !tenant?.BusinessName ? (
                <div className="logo-placeholder" aria-hidden="true">
                  <FiShoppingBag />
                </div>
              ) : null}
              {tenant?.BusinessName && effectiveBrandLayout !== "logo-only" && (
                <span className="company-name">
                  <span className="company-name__lines">
                    {storeNameLine1 && (
                      <span className="company-name__line1">
                        {storeNameLine1}
                      </span>
                    )}
                    {storeNameLine1 && storeNameLine2 && separatorGlyph && (
                      <span
                        className="company-name__separator"
                        aria-hidden="true"
                      >
                        {separatorGlyph}
                      </span>
                    )}
                    {storeNameLine2 && (
                      <span className="company-name__line2">
                        {storeNameLine2}
                      </span>
                    )}
                  </span>
                  {storeSlogan &&
                    separatorGlyph &&
                    (storeNameLine1 || storeNameLine2) && (
                      <span
                        className="company-name__separator company-name__separator--slogan"
                        aria-hidden="true"
                      >
                        {separatorGlyph}
                      </span>
                    )}
                  {storeSlogan && (
                    <span className="company-name__slogan">{storeSlogan}</span>
                  )}
                </span>
              )}
            </Link>
          </div>

          {/* Desktop navigation */}
          <div
            className="nav-links-right"
            role="group"
            aria-label="Store pages"
          >
            {rootNavLinks.map((link) => {
              const children = getNavChildren(link);
              const isOpen = openNavigationDropdown === link.id;
              const hasActiveChild = children.some((child) =>
                isActive(child.path),
              );
              const linkActive = isActive(link.path) || hasActiveChild;
              return (
                <div
                  className={`nav-link-group${children.length ? " has-dropdown" : ""}${isOpen ? " is-open" : ""}`}
                  key={link.id || `${link.path}-${link.label}`}
                  onMouseEnter={() =>
                    children.length && setOpenNavigationDropdown(link.id)
                  }
                  onMouseLeave={() =>
                    children.length && setOpenNavigationDropdown(null)
                  }
                  onFocus={() =>
                    children.length && setOpenNavigationDropdown(link.id)
                  }
                  onBlur={(event) => {
                    if (
                      children.length &&
                      !event.currentTarget.contains(event.relatedTarget)
                    ) {
                      setOpenNavigationDropdown(null);
                    }
                  }}
                >
                  <Link
                    to={link.path}
                    className={linkActive ? "active" : ""}
                    aria-current={isActive(link.path) ? "page" : undefined}
                    onClick={handleNavClick}
                  >
                    {link.label}
                  </Link>
                  {children.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="nav-dropdown-toggle"
                        aria-label={`Toggle ${link.label} submenu`}
                        aria-expanded={isOpen}
                        onClick={() => toggleNavigationDropdown(link.id)}
                      >
                        <FiChevronDown aria-hidden="true" />
                      </button>
                      <div className="nav-dropdown-menu">
                        {children.map((child) => (
                          <Link
                            key={child.id || `${child.path}-${child.label}`}
                            to={child.path}
                            className={isActive(child.path) ? "active" : ""}
                            aria-current={
                              isActive(child.path) ? "page" : undefined
                            }
                            onClick={handleNavClick}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="nav-right-icons">
            <form
              className="nav-store-search"
              role="search"
              onSubmit={handleStoreSearch}
            >
              <label className="nav-store-search-label" htmlFor="store-nav-search">
                Search products
              </label>
              <input
                id="store-nav-search"
                type="search"
                placeholder="Search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              <button type="submit" aria-label="Search products">
                <FiSearch aria-hidden="true" />
              </button>
            </form>
            {/* Cart */}
            <div className="cart-dropdown" ref={cartDropdownRef}>
              <button
                type="button"
                className="cart-icon-link"
                onClick={toggleCartDropdown}
                aria-label={`Shopping bag, ${cartCount} ${cartCount === 1 ? "item" : "items"}`}
                aria-haspopup="true"
                aria-expanded={showCartDropdown}
              >
                <FiShoppingBag size={22} />
                {cartCount > 0 && (
                  <span className="cart-badge-desktop" aria-hidden="true">
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
                {expiringPromotions.length > 0 && (
                  <span className="cart-expiry-indicator" aria-hidden="true">
                    <FiAlertTriangle />
                  </span>
                )}
              </button>

              {showCartDropdown && (
                <div className="cart-dropdown-menu nav-popover">
                  <div className="cart-dropdown-header">
                    <h3>Shopping Bag</h3>
                    <span>
                      {cartCount} {cartCount === 1 ? "item" : "items"}
                    </span>
                  </div>

                  {expiringPromotions.length > 0 && (
                    <div className="cart-dropdown-expiry-warning" role="status">
                      <FiAlertTriangle aria-hidden="true" />
                      <span>
                        {expiringPromotions.length} promotion
                        {expiringPromotions.length > 1 ? "s" : ""} expiring soon
                      </span>
                    </div>
                  )}

                  {loading ? (
                    <div className="cart-dropdown-loading" role="status">
                      <div className="cart-dropdown-spinner" />
                      <p>Loading…</p>
                    </div>
                  ) : cartItems.length === 0 ? (
                    <div className="cart-dropdown-empty">
                      <FiShoppingBag aria-hidden="true" />
                      <p>Your bag is empty</p>
                      <button
                        type="button"
                        className="cart-dropdown-shop-now"
                        onClick={() => handleNavigate("/shop")}
                      >
                        Start shopping
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="cart-dropdown-items">
                        {dropdownItems.map((item) => {
                          const productId = item.ProductID || item.productId;
                          const quantity = item.Quantity || 0;
                          const itemPrice =
                            item.UnitPrice ||
                            item.Price ||
                            item.SellingPrice ||
                            0;
                          const originalPrice = item.OriginalPrice || itemPrice;
                          const itemTotal = itemPrice * quantity;
                          const originalTotal = originalPrice * quantity;
                          const isUpdating = isItemUpdating(productId);
                          const hasPromo = hasPromotion(item);
                          const promotion = item.Promotion;

                          return (
                            <div
                              key={productId}
                              className={`cart-dropdown-item ${hasPromo ? "has-promotion" : ""}`}
                            >
                              <div className="cart-dropdown-item-image">
                                <img
                                  src={
                                    item.ImageUrl || "/images/placeholder.jpg"
                                  }
                                  alt={item.Name || "Product"}
                                />
                                {hasPromo && (
                                  <div className="cart-dropdown-discount-badge">
                                    -{getDiscountPercent(item)}%
                                  </div>
                                )}
                              </div>

                              <div className="cart-dropdown-item-info">
                                <div
                                  className="cart-dropdown-item-name"
                                  title={item.Name}
                                >
                                  {item.Name}
                                </div>

                                <div className="cart-dropdown-item-price">
                                  {hasPromo ? (
                                    <>
                                      <span className="current-price">
                                        {formatPrice(itemPrice)}
                                      </span>
                                      <span className="original-price">
                                        {formatPrice(originalPrice)}
                                      </span>
                                    </>
                                  ) : (
                                    <span>{formatPrice(itemPrice)}</span>
                                  )}
                                </div>

                                {promotion?.Code && (
                                  <div className="cart-dropdown-promo-code">
                                    <FiTag size={10} aria-hidden="true" />
                                    <span>{promotion.Code}</span>
                                    {promotion.EndDate && (
                                      <span className="promo-expiry">
                                        · Ends{" "}
                                        {new Date(
                                          promotion.EndDate,
                                        ).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                )}

                                <div className="cart-dropdown-item-actions">
                                  <button
                                    type="button"
                                    className="cart-dropdown-qty-btn"
                                    aria-label={`Decrease quantity of ${item.Name}`}
                                    onClick={() =>
                                      handleUpdateQuantity(
                                        productId,
                                        quantity,
                                        -1,
                                      )
                                    }
                                    disabled={isUpdating}
                                  >
                                    <FiMinus size={12} />
                                  </button>
                                  <span
                                    className="cart-dropdown-qty"
                                    aria-live="polite"
                                  >
                                    {quantity}
                                  </span>
                                  <button
                                    type="button"
                                    className="cart-dropdown-qty-btn"
                                    aria-label={`Increase quantity of ${item.Name}`}
                                    onClick={() =>
                                      handleUpdateQuantity(
                                        productId,
                                        quantity,
                                        1,
                                      )
                                    }
                                    disabled={isUpdating}
                                  >
                                    <FiPlus size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    className="cart-dropdown-remove"
                                    aria-label={`Remove ${item.Name} from bag`}
                                    onClick={() => handleRemoveItem(productId)}
                                    disabled={isUpdating}
                                  >
                                    {isUpdating ? (
                                      <div className="cart-dropdown-spinner-small" />
                                    ) : (
                                      <FiTrash2 size={14} />
                                    )}
                                  </button>
                                </div>
                              </div>

                              <div className="cart-dropdown-item-total">
                                {hasPromo ? (
                                  <>
                                    <span className="current-total">
                                      {formatPrice(itemTotal)}
                                    </span>
                                    <span className="original-total">
                                      {formatPrice(originalTotal)}
                                    </span>
                                  </>
                                ) : (
                                  <span>{formatPrice(itemTotal)}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {remainingCount > 0 && (
                        <div className="cart-dropdown-more">
                          <p>
                            + {remainingCount} more{" "}
                            {remainingCount === 1 ? "item" : "items"} in bag
                          </p>
                        </div>
                      )}

                      {cartSavings > 0 && (
                        <div className="cart-dropdown-savings">
                          <span className="savings-label">You save</span>
                          <span className="savings-amount">
                            -{formatPrice(cartSavings)}
                          </span>
                        </div>
                      )}

                      <div className="cart-dropdown-footer">
                        <div className="cart-dropdown-total">
                          <span>Total</span>
                          <strong>{formatPrice(cartTotal)}</strong>
                        </div>
                        <div className="cart-dropdown-buttons">
                          <button
                            type="button"
                            className="cart-dropdown-view-cart"
                            onClick={() => handleNavigate("/cart")}
                          >
                            View Bag
                          </button>
                          <button
                            type="button"
                            className="cart-dropdown-checkout"
                            onClick={() => handleNavigate("/checkout")}
                          >
                            Checkout
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Account */}
            <div className="user-dropdown" ref={userDropdownRef}>
              <button
                type="button"
                className="user-icon-btn"
                onClick={toggleUserDropdown}
                aria-label={user ? "Account menu" : "Sign in or create account"}
                aria-haspopup="true"
                aria-expanded={showUserDropdown}
              >
                {user ? (
                  <span className="user-avatar" aria-hidden="true">
                    {userInitial}
                  </span>
                ) : (
                  <FiUser size={22} />
                )}
              </button>

              {showUserDropdown && (
                <div className="user-dropdown-menu nav-popover">
                  {user ? (
                    <>
                      <div className="dropdown-user-info">
                        <div className="dropdown-user-name">
                          {userDisplayName}
                          <span
                            className={`verification-badge ${user.emailVerified ? "verified" : "unverified"}`}
                          >
                            {user.emailVerified ? "Verified" : "Unverified"}
                          </span>
                        </div>
                        <div className="dropdown-user-email">{user.email}</div>
                        {!user.emailVerified && (
                          <div className="verification-warning-small">
                            <FiMail size={12} aria-hidden="true" />
                            <span>Please verify your email</span>
                          </div>
                        )}
                      </div>
                      <Link to="/dashboard" onClick={handleNavClick}>
                        <FiGrid aria-hidden="true" /> Dashboard
                      </Link>
                      <Link to={ordersDestination} onClick={handleNavClick}>
                        <FiPackage aria-hidden="true" /> My Orders
                      </Link>
                      <Link
                        to={notificationsDestination}
                        onClick={handleNavClick}
                      >
                        <FiBell aria-hidden="true" /> Notifications
                      </Link>
                      <div className="dropdown-divider" />
                      <button
                        type="button"
                        className="logout-btn"
                        onClick={() => {
                          setShowUserDropdown(false);
                          setShowLogoutModal(true);
                        }}
                      >
                        <FiLogOut aria-hidden="true" /> Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={handleNavClick}>
                        <FiUser aria-hidden="true" /> Sign In
                      </Link>
                      <Link to="/register" onClick={handleNavClick}>
                        <FiUserPlus aria-hidden="true" /> Create Account
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Mobile cart */}
            <div className="nav-cart-mobile">
              <Link
                to="/cart"
                onClick={handleNavClick}
                aria-label={`Shopping bag, ${cartCount} ${cartCount === 1 ? "item" : "items"}`}
              >
                <FiShoppingBag size={24} />
                {cartCount > 0 && (
                  <span className="cart-badge" aria-hidden="true">
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
                {expiringPromotions.length > 0 && (
                  <span
                    className="cart-expiry-indicator-mobile"
                    aria-hidden="true"
                  >
                    <FiAlertTriangle />
                  </span>
                )}
              </Link>
            </div>

            {/* Menu toggle */}
            <button
              type="button"
              ref={menuToggleRef}
              className="menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              aria-controls="store-mobile-navigation"
            >
              <FiMenu size={24} />
            </button>
          </div>
        </div>

        {/* Overlay */}
        <button
          type="button"
          className={`mobile-menu-overlay ${isMenuOpen ? "active" : ""}`}
          data-mobile-menu-overlay={mobileLayout.menuOverlay}
          onClick={handleNavClick}
          aria-label="Close navigation menu"
          tabIndex={-1}
        />

        {/* Mobile drawer */}
        <aside
          id="store-mobile-navigation"
          className={`mobile-menu ${isMenuOpen ? "active" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Store navigation"
          aria-hidden={!isMenuOpen}
        >
          <div className="mobile-menu__header">
            <div className="mobile-menu__identity">
              {tenant?.Logo && (
                <img
                  src={tenant.Logo}
                  className="mobile-menu__logo"
                  alt=""
                  aria-hidden="true"
                />
              )}
              <div>
                <span className="mobile-menu__eyebrow">Explore</span>
                <strong className="mobile-menu__store-name">
                  {tenant?.BusinessName || "Store"}
                </strong>
              </div>
            </div>
            <button
              type="button"
              ref={menuCloseRef}
              className="mobile-menu-close"
              onClick={handleNavClick}
              aria-label="Close navigation menu"
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="mobile-menu-links">
            <form
              className="nav-store-search nav-store-search--mobile"
              role="search"
              onSubmit={handleStoreSearch}
            >
              <label
                className="nav-store-search-label"
                htmlFor="store-nav-search-mobile"
              >
                Search products
              </label>
              <input
                id="store-nav-search-mobile"
                type="search"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              <button type="submit" aria-label="Search products">
                <FiSearch aria-hidden="true" />
              </button>
            </form>
            {rootNavLinks.map((link) => {
              const children = getNavChildren(link);
              const isOpen = openNavigationDropdown === link.id;
              const linkActive =
                isActive(link.path) ||
                children.some((child) => isActive(child.path));
              return (
                <div
                  className={`mobile-nav-group${isOpen ? " is-open" : ""}`}
                  key={link.id || `${link.path}-${link.label}`}
                >
                  <div className="mobile-nav-group__heading">
                    <Link
                      to={link.path}
                      onClick={handleNavClick}
                      className={linkActive ? "active" : ""}
                      aria-current={isActive(link.path) ? "page" : undefined}
                    >
                      {link.label}
                    </Link>
                    {children.length > 0 && (
                      <button
                        type="button"
                        className="mobile-nav-group__toggle"
                        aria-label={`Toggle ${link.label} submenu`}
                        aria-expanded={isOpen}
                        onClick={() => toggleNavigationDropdown(link.id)}
                      >
                        <FiChevronDown aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  {children.length > 0 && isOpen && (
                    <div className="mobile-nav-submenu">
                      {children.map((child) => (
                        <Link
                          key={child.id || `${child.path}-${child.label}`}
                          to={child.path}
                          onClick={handleNavClick}
                          className={isActive(child.path) ? "active" : ""}
                          aria-current={
                            isActive(child.path) ? "page" : undefined
                          }
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="mobile-menu__section-label">Your account</div>
            <div className="dropdown-divider" />

            <Link
              to="/cart"
              onClick={handleNavClick}
              className={isActive("/cart") ? "active" : ""}
            >
              <FiShoppingBag aria-hidden="true" /> Bag ({cartCount})
              {expiringPromotions.length > 0 && (
                <span
                  className="mobile-expiry-badge"
                  title="A promotion in your bag is expiring soon"
                >
                  <FiAlertTriangle aria-hidden="true" />
                </span>
              )}
            </Link>

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={handleNavClick}
                  className={isActive("/dashboard") ? "active" : ""}
                >
                  <FiGrid aria-hidden="true" /> Dashboard
                </Link>
                <Link
                  to={ordersDestination}
                  onClick={handleNavClick}
                  className={isActive(ordersDestination) ? "active" : ""}
                >
                  <FiPackage aria-hidden="true" /> My Orders
                </Link>
                <Link
                  to={notificationsDestination}
                  onClick={handleNavClick}
                  className={isActive(notificationsDestination) ? "active" : ""}
                >
                  <FiBell aria-hidden="true" /> Notifications
                </Link>
                {user.role === "Admin" && (
                  <Link
                    to="/admin"
                    onClick={handleNavClick}
                    className={isActive("/admin") ? "active" : ""}
                  >
                    <FiGrid aria-hidden="true" /> Admin Panel
                  </Link>
                )}
                {!user.emailVerified && (
                  <div className="mobile-verification-warning" role="status">
                    <FiMail aria-hidden="true" /> Please verify your email
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    handleNavClick();
                    setShowLogoutModal(true);
                  }}
                  className="logout-btn-mobile"
                >
                  <FiLogOut aria-hidden="true" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={handleNavClick}
                  className={isActive("/login") ? "active" : ""}
                >
                  <FiUser aria-hidden="true" /> Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={handleNavClick}
                  className={isActive("/register") ? "active" : ""}
                >
                  <FiUserPlus aria-hidden="true" /> Create Account
                </Link>
              </>
            )}
          </div>
        </aside>
      </nav>

      {signedOutNotice && !user && (
        <div className="signed-out-notice" role="status">
          <span>{signedOutNotice}</span>
          <Link to="/login" onClick={() => setSignedOutNotice("")}>
            Sign in
          </Link>
          <button
            type="button"
            onClick={() => setSignedOutNotice("")}
            aria-label="Dismiss"
          >
            <FiX />
          </button>
        </div>
      )}

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={userDisplayName}
        isLoggingOut={isLoggingOut}
      />
    </>
  );
};

export default Navbar;
